/**
 * StaffComposer — calm message form for staff → families.
 * Per-group targeting + scheduled sends. Extracted from Staff.jsx for clarity.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Pin, Send } from "lucide-react";

export default function StaffComposer({ seed, groups, onCancel, onSend }) {
  const [title, setTitle] = useState(seed?.title || "");
  const [body, setBody] = useState(seed?.body || "");
  const [target, setTarget] = useState("all");
  const [pinned, setPinned] = useState(false);
  const [category, setCategory] = useState("general");
  const [replyMode, setReplyMode] = useState("broadcast"); // broadcast | open | quiet
  const [scheduleMode, setScheduleMode] = useState("now"); // now | in1h | in4h | tomorrow9 | custom
  const [customDate, setCustomDate] = useState("");
  const [customTime, setCustomTime] = useState("");
  const [busy, setBusy] = useState(false);

  const computeScheduledFor = () => {
    if (scheduleMode === "now") return null;
    const d = new Date();
    if (scheduleMode === "in1h") d.setHours(d.getHours() + 1);
    else if (scheduleMode === "in4h") d.setHours(d.getHours() + 4);
    else if (scheduleMode === "tomorrow9") {
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
    } else if (scheduleMode === "custom") {
      if (!customDate || !customTime) return "invalid";
      const combined = new Date(`${customDate}T${customTime}:00`);
      if (combined < new Date()) return "invalid";
      return combined.toISOString();
    }
    return d.toISOString();
  };

  const submit = async () => {
    if (!title.trim() || !body.trim()) {
      toast("Please add a title and a message.");
      return;
    }
    const scheduled = computeScheduledFor();
    if (scheduled === "invalid") {
      toast("Pick a future date and time.");
      return;
    }
    setBusy(true);
    try {
      await onSend({
        title: title.trim(),
        body: body.trim(),
        category,
        pinned,
        target_group_name: target === "all" ? null : target,
        scheduled_for: scheduled,
        reply_mode: replyMode,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="staff-composer"
      className="fixed inset-0 z-[60] bg-[#FBF9F6]/95 backdrop-blur-xl flex items-center justify-center px-6 overflow-y-auto"
    >
      <div className="bg-white rounded-3xl border hairline p-7 max-w-xl w-full fade-up my-10">
        <span className="eyebrow">Calm message to families</span>
        <h3 className="mt-3 font-serif text-2xl text-[#2C2B29]">
          {seed?.title ? "Tweak & send" : "New message"}
        </h3>

        <label className="block mt-5">
          <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
            Title
          </span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="staff-composer-title"
            placeholder="A short, calm headline"
            maxLength={120}
            className="mt-2 w-full bg-[#F2EFEA] rounded-2xl px-4 py-3 text-sm outline-none focus:bg-white border hairline"
          />
        </label>

        <label className="block mt-4">
          <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
            Message
          </span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid="staff-composer-body"
            rows={4}
            placeholder="What you'd like families to know, gently."
            maxLength={2000}
            className="mt-2 w-full bg-[#F2EFEA] rounded-2xl px-4 py-3 text-sm outline-none focus:bg-white border hairline"
          />
        </label>

        <div className="mt-5">
          <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
            Send to
          </span>
          <div
            className="mt-2 flex flex-wrap gap-2"
            data-testid="staff-composer-target"
          >
            <button
              type="button"
              onClick={() => setTarget("all")}
              data-testid="staff-composer-target-all"
              aria-pressed={target === "all"}
              className={
                "px-3 py-1.5 rounded-full text-xs transition-colors " +
                (target === "all"
                  ? "bg-[#132619] text-white"
                  : "bg-[#F2EFEA] border hairline text-[#6A6863] hover:text-[#2C2B29]")
              }
            >
              All families
            </button>
            {groups.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setTarget(g)}
                data-testid={`staff-composer-target-${g.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                aria-pressed={target === g}
                className={
                  "px-3 py-1.5 rounded-full text-xs transition-colors " +
                  (target === g
                    ? "bg-[#132619] text-white"
                    : "bg-[#F2EFEA] border hairline text-[#6A6863] hover:text-[#2C2B29]")
                }
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
            When
          </span>
          <div
            className="mt-2 flex flex-wrap gap-2"
            data-testid="staff-composer-schedule"
          >
            {[
              { id: "now", label: "Now" },
              { id: "in1h", label: "In 1 hour" },
              { id: "in4h", label: "In 4 hours" },
              { id: "tomorrow9", label: "Tomorrow 9 AM" },
              { id: "custom", label: "Choose…" },
            ].map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setScheduleMode(s.id)}
                data-testid={`staff-composer-when-${s.id}`}
                aria-pressed={scheduleMode === s.id}
                className={
                  "px-3 py-1.5 rounded-full text-xs transition-colors " +
                  (scheduleMode === s.id
                    ? "bg-[#132619] text-white"
                    : "bg-[#F2EFEA] border hairline text-[#6A6863] hover:text-[#2C2B29]")
                }
              >
                {s.label}
              </button>
            ))}
          </div>
          {scheduleMode === "custom" && (
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                data-testid="staff-composer-custom-date"
                className="bg-[#F2EFEA] border hairline rounded-full px-3 py-1.5 text-sm outline-none"
              />
              <input
                type="time"
                value={customTime}
                onChange={(e) => setCustomTime(e.target.value)}
                data-testid="staff-composer-custom-time"
                className="bg-[#F2EFEA] border hairline rounded-full px-3 py-1.5 text-sm outline-none"
              />
            </div>
          )}
        </div>

        <div className="mt-5">
          <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
            Reply mode
          </span>
          <div
            className="mt-2 flex flex-wrap gap-2"
            data-testid="staff-composer-reply-mode"
          >
            {[
              {
                id: "broadcast",
                label: "Broadcast",
                hint: "One-way · families read only",
              },
              {
                id: "open",
                label: "Open thread",
                hint: "Families reply · everyone in the group sees",
              },
              {
                id: "quiet",
                label: "Ask quietly",
                hint: "Families reply privately · only staff sees",
              },
            ].map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setReplyMode(m.id)}
                data-testid={`staff-composer-reply-${m.id}`}
                aria-pressed={replyMode === m.id}
                className={
                  "px-3 py-1.5 rounded-full text-xs transition-colors " +
                  (replyMode === m.id
                    ? "bg-[#132619] text-white"
                    : "bg-[#F2EFEA] border hairline text-[#6A6863] hover:text-[#2C2B29]")
                }
                title={m.hint}
              >
                {m.label}
              </button>
            ))}
          </div>
          <p
            className="mt-2 text-[11px] italic text-[#6A6863] font-light"
            data-testid="staff-composer-reply-hint"
          >
            {replyMode === "broadcast"
              ? "Families read this gently — no reply."
              : replyMode === "open"
              ? "Families can reply, and everyone in the group sees the conversation."
              : "Families can reply, but only staff sees their reply."}
          </p>
        </div>

        <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={pinned}
              onChange={(e) => setPinned(e.target.checked)}
              data-testid="staff-composer-pinned"
              className="accent-[#1A1B2E]"
            />
            <Pin size={11} strokeWidth={1.5} className="text-[#6A6863]" />
            <span className="tracking-[0.18em] uppercase text-[#6A6863]">
              Pin on families' Home
            </span>
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="staff-composer-category"
            className="bg-[#F2EFEA] border hairline rounded-full px-3 py-1.5 text-xs outline-none"
          >
            <option value="general">General</option>
            <option value="closure">Closure</option>
            <option value="reminder">Reminder</option>
            <option value="celebration">Celebration</option>
            <option value="weather">Weather</option>
          </select>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            data-testid="staff-composer-cancel"
            className="btn-ghost !py-2 !px-4 !text-xs"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="staff-composer-send"
            className="btn-primary !py-2 !px-4 !text-xs"
          >
            {busy
              ? "Sending…"
              : scheduleMode === "now"
              ? "Send softly"
              : "Schedule gently"}
            <Send size={12} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
